import { ApolloCache, Reference } from "@apollo/client";
import * as Sentry from "@sentry/browser";
import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { errorToast, successToast, useToast } from "../../../components";
import {
  AiInterviewerRoleName,
  useAiInterviewerLazyQuery,
  useOrgUsersPaginatedLazyQuery,
  useRemoveAiInterviewerMembershipMutation,
  useSetAiInterviewerMembershipsMutation,
} from "../../graphql";
import { ShareModalV2Props } from "../ShareModalV2";
import { AiInterviewerMembershipRole } from "./types";

export type UseAiInterviewerShareModalV2Params = {
  aiInterviewerId: string;
  onClose(): void;
};

export type UseAiInterviewerShareModalV2Result = {
  shareModalProps: Omit<
    ShareModalV2Props<AiInterviewerMembershipRole>,
    "isOpen" | "onClose"
  >;
  loadData: () => Promise<any>;
  loading: boolean;
};

const INFO_TEXT =
  "All shared users will be able to view interviews & evaluations for this AI interviewer.";

/**
 * Hook for AI Interviewer sharing with ShareModalV2
 *
 * Usage:
 * ```tsx
 * const { shareModalProps, loadData, loading } = useAiInterviewerShareModalV2({
 *   aiInterviewerId: "123",
 *   onClose: () => setIsOpen(false),
 * });
 * ```
 */
type ShareOperation =
  | { type: "idle" }
  | { type: "share"; loading: true }
  | { type: "change_role"; loading: true }
  | { type: "remove"; loading: true };

export function useAiInterviewerShareModalV2({
  aiInterviewerId,
  onClose,
}: UseAiInterviewerShareModalV2Params): UseAiInterviewerShareModalV2Result {
  const toast = useToast();
  const [shareOperation, setShareOperation] = useState<ShareOperation>({
    type: "idle",
  });

  const shareLoading = shareOperation.type !== "idle";

  // Query data
  const [loadAiInterviewer, { data, loading, error: aiInterviewerError }] =
    useAiInterviewerLazyQuery();
  const loadData = useCallback(
    () => loadAiInterviewer({ variables: { aiInterviewerId } }),
    [loadAiInterviewer, aiInterviewerId]
  );

  // Query for shareable users
  const [
    loadShareableUsers,
    {
      data: currentUsersData,
      loading: usersLoading,
      previousData: previousUsersData,
      error: usersQueryError,
    },
  ] = useOrgUsersPaginatedLazyQuery();
  // `currentUsersData` will flip to `undefined` when the request is in-flight,
  // so fallback to `previousUsersData` to prevent UI flicker
  const usersData = currentUsersData ?? previousUsersData;
  const usersError = Boolean(usersQueryError);

  const reportedAiInterviewerErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (!aiInterviewerError) {
      return;
    }
    const errorKey = aiInterviewerError.message;
    if (reportedAiInterviewerErrorRef.current === errorKey) {
      return;
    }
    reportedAiInterviewerErrorRef.current = errorKey;
    Sentry.captureException(aiInterviewerError);
    onClose();
    errorToast(toast, `There was a problem loading the AI Interviewer`);
  }, [aiInterviewerError, onClose, toast]);

  const reportedUsersErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (!usersQueryError) {
      return;
    }
    const errorKey = usersQueryError.message;
    if (reportedUsersErrorRef.current === errorKey) {
      return;
    }
    reportedUsersErrorRef.current = errorKey;
    Sentry.captureException(usersQueryError);
    errorToast(toast, `There was a problem loading users`);
  }, [usersQueryError, toast]);

  const onSearchChange = useCallback(
    async (searchTerm: string) => {
      await loadShareableUsers({
        variables: {
          pagination: {
            page: 1,
            limit: 100,
            searchTerm,
          },
        },
      });
    },
    [loadShareableUsers]
  );

  // Mutations
  const [setMemberships] = useSetAiInterviewerMembershipsMutation({
    onCompleted: () => {
      const message =
        shareOperation.type === "change_role"
          ? "Successfully updated role"
          : "Successfully shared interviewer";
      successToast(toast, message);
      loadData(); // Refetch interviewer data
      setShareOperation({ type: "idle" });
    },
    onError: (err) => {
      Sentry.captureException(err);
      const action =
        shareOperation.type === "change_role" ? "update role" : "share";
      errorToast(toast, `Failed to ${action}: ${err.message}`);
      setShareOperation({ type: "idle" });
    },
  });

  const [removeMembership] = useRemoveAiInterviewerMembershipMutation({
    update: (cache: ApolloCache<any>, { data }, { variables }) => {
      // Optimistically remove the membership from cache
      const membershipId = variables?.membershipId;
      if (data?.removeAiInterviewerMembership?.success && membershipId) {
        cache.modify({
          id: cache.identify({
            __typename: "AiInterviewer",
            id: aiInterviewerId,
          }),
          fields: {
            memberships: (
              existing: ReadonlyArray<Reference>,
              { readField }
            ) => {
              return existing.filter(
                (membership) => readField("id", membership) !== membershipId
              );
            },
          },
        });
      }
    },
    onCompleted: () => {
      successToast(toast, "Successfully removed sharing");
      loadData(); // Refetch interviewer data to ensure consistency
      setShareOperation({ type: "idle" });
    },
    onError: (err) => {
      Sentry.captureException(err);
      errorToast(toast, `Failed to remove sharing: ${err.message}`);
      setShareOperation({ type: "idle" });
      // Refetch on error to restore correct state
      loadData();
    },
  });

  const aiInterviewer = data?.aiInterviewer;
  const shareableUsers =
    usersData?.currentUser?.organization?.usersPaginated?.results || [];

  // Convert AiInterviewerMembershipRole (string literal) to AiInterviewerRoleName (enum)
  const roleToGraphQLRole = (
    role: AiInterviewerMembershipRole
  ): AiInterviewerRoleName => {
    return role === "EDITOR"
      ? AiInterviewerRoleName.Editor
      : AiInterviewerRoleName.Viewer;
  };

  // Transform data for ShareModalV2
  const shareModalProps: Omit<
    ShareModalV2Props<AiInterviewerMembershipRole>,
    "isOpen" | "onClose"
  > = useMemo(() => {
    if (!aiInterviewer) {
      return {
        entityName: "AI Interviewer",
        owner: {
          id: "",
          firstName: "",
          lastName: "",
          email: "",
        },
        shares: [],
        shareableUsers: [],
        availableRoles: [
          { value: "EDITOR" as AiInterviewerMembershipRole, label: "Editor" },
          { value: "VIEWER" as AiInterviewerMembershipRole, label: "Viewer" },
        ],
        defaultRole: "VIEWER" as AiInterviewerMembershipRole,
        infoText: INFO_TEXT,
        canShare: true,
        shareUrl: `${window.location.origin}/ai-interviewer/${aiInterviewerId}`,
        onShare: async () => {
          // No-op when data is not loaded
        },
        onRemoveShare: async () => {
          // No-op when data is not loaded
        },
      };
    }

    const ownerUser =
      aiInterviewer.initialVersion?.createdBy ??
      aiInterviewer.currentVersion?.createdBy;

    // Some GraphQL fragments only include a subset of user fields; when present,
    // preserve identity fields for better owner display in the modal.
    const ownerDetails = ownerUser as
      | (typeof ownerUser & {
          firstName?: string | null;
          lastName?: string | null;
          email?: string | null;
          fullName?: string | null;
          profilePicUrl?: string | null;
        })
      | undefined;

    const resolvedOwner = ownerUser
      ? {
          id: ownerUser.id,
          firstName: ownerDetails?.firstName ?? null,
          lastName: ownerDetails?.lastName ?? null,
          email: ownerDetails?.email ?? "",
          fullName: ownerDetails?.fullName ?? undefined,
          profilePicUrl: ownerDetails?.profilePicUrl ?? null,
        }
      : {
          id: "",
          firstName: null,
          lastName: null,
          email: "",
          fullName: "Unknown",
          profilePicUrl: null,
        };

    return {
      entityName: "AI Interviewer",
      entityTitle: aiInterviewer.displayName || aiInterviewer.name,
      infoText: INFO_TEXT,
      owner: resolvedOwner,
      shares: (aiInterviewer.memberships || []).map((membership) => ({
        id: membership.id,
        sharedTo: {
          id: membership.user.id,
          firstName: membership.user.firstName ?? null,
          lastName: membership.user.lastName ?? null,
          email: membership.user.email,
        },
        role: membership.role as AiInterviewerMembershipRole,
      })),
      shareableUsers: shareableUsers.map((user) => ({
        id: user.id,
        firstName: user.firstName ?? null,
        lastName: user.lastName ?? null,
        email: user.email,
        fullName: user.fullName,
        profilePicUrl: user.profilePicUrl ?? null,
      })),
      availableRoles: [
        { value: "EDITOR" as AiInterviewerMembershipRole, label: "Editor" },
        { value: "VIEWER" as AiInterviewerMembershipRole, label: "Viewer" },
      ],
      defaultRole: "VIEWER" as AiInterviewerMembershipRole,
      specialAccessGroups: [
        {
          id: "all-site-admins",
          label: "All site admins",
          role: "EDITOR" as AiInterviewerMembershipRole,
          canRemove: false,
          avatarIcon: createElement(
            "svg",
            {
              xmlns: "http://www.w3.org/2000/svg",
              width: 14,
              height: 13,
              viewBox: "0 0 14 13",
              fill: "none",
            },
            createElement("path", {
              fillRule: "evenodd",
              clipRule: "evenodd",
              d: "M6.99951 1C6.17108 1 5.49951 1.67157 5.49951 2.5C5.49951 3.32843 6.17108 4 6.99951 4C7.82794 4 8.49951 3.32843 8.49951 2.5C8.49951 1.67157 7.82794 1 6.99951 1ZM4.49951 2.5C4.49951 1.11929 5.6188 0 6.99951 0C8.38022 0 9.49951 1.11929 9.49951 2.5C9.49951 3.88071 8.38022 5 6.99951 5C5.6188 5 4.49951 3.88071 4.49951 2.5ZM2.49951 3.5C1.94723 3.5 1.49951 3.94772 1.49951 4.5C1.49951 5.05229 1.94723 5.5 2.49951 5.5C3.0518 5.5 3.49951 5.05229 3.49951 4.5C3.49951 3.94772 3.0518 3.5 2.49951 3.5ZM0.499512 4.5C0.499512 3.39543 1.39494 2.5 2.49951 2.5C3.60408 2.5 4.49951 3.39543 4.49951 4.5C4.49951 5.60457 3.60408 6.5 2.49951 6.5C1.39494 6.5 0.499512 5.60457 0.499512 4.5ZM11.4995 3.5C10.9472 3.5 10.4995 3.94772 10.4995 4.5C10.4995 5.05229 10.9472 5.5 11.4995 5.5C12.0518 5.5 12.4995 5.05229 12.4995 4.5C12.4995 3.94772 12.0518 3.5 11.4995 3.5ZM9.49951 4.5C9.49951 3.39543 10.3949 2.5 11.4995 2.5C12.6041 2.5 13.4995 3.39543 13.4995 4.5C13.4995 5.60457 12.6041 6.5 11.4995 6.5C10.3949 6.5 9.49951 5.60457 9.49951 4.5ZM6.99951 7C5.76034 7 4.67116 7.64361 4.04852 8.61714C3.704 9.15582 3.50301 9.79483 3.49956 10.482L3.49951 10.5C3.49951 10.5457 3.50039 10.5912 3.50212 10.6364C4.54591 11.1877 5.73559 11.5 6.99951 11.5C8.26343 11.5 9.45311 11.1877 10.4969 10.6364C10.4986 10.5912 10.4995 10.5457 10.4995 10.5L10.4995 10.4819C10.496 9.79495 10.2951 9.15609 9.95074 8.6175C9.32814 7.64377 8.23884 7 6.99951 7ZM11.472 10.9999C11.4541 11.158 11.3621 11.2982 11.224 11.3774C9.97912 12.0917 8.53623 12.5 6.99951 12.5C5.46279 12.5 4.0199 12.0917 2.77501 11.3774C2.63696 11.2982 2.5449 11.158 2.52702 10.9999C2.51802 11 2.50901 11 2.5 11C1.74376 11 1.01666 10.8706 0.340285 10.6324C0.153875 10.5668 0.0236256 10.3975 0.00794876 10.2005C0.00267808 10.1343 0 10.0674 0 10C0 8.61929 1.11929 7.5 2.5 7.5C2.84649 7.5 3.17713 7.57073 3.47771 7.69856C4.30158 6.6642 5.57285 6 6.99951 6C8.4263 6 9.69767 6.66432 10.5215 7.69883C10.8223 7.57083 11.1532 7.5 11.4999 7.5C12.8806 7.5 13.9999 8.61929 13.9999 10C13.9999 10.0674 13.9972 10.1343 13.9919 10.2005C13.9763 10.3975 13.846 10.5668 13.6596 10.6324C12.9832 10.8706 12.2561 11 11.4999 11C11.4906 11 11.4813 11 11.472 10.9999ZM11.063 8.56448C11.2746 9.00789 11.4157 9.49132 11.472 9.99993C11.4813 9.99998 11.4906 10 11.4999 10C12.0155 10 12.5139 9.92917 12.9863 9.79694C12.8871 9.06455 12.2594 8.5 11.4999 8.5C11.3477 8.5 11.2011 8.52255 11.063 8.56448ZM2.93612 8.56425C2.79829 8.52246 2.65195 8.5 2.5 8.5C1.74044 8.5 1.11275 9.06455 1.01363 9.79694C1.48595 9.92917 1.98436 10 2.5 10C2.50901 10 2.51802 9.99998 2.52702 9.99994C2.58333 9.49123 2.72452 9.00772 2.93612 8.56425Z",
              fill: "white",
            })
          ),
        },
      ],
      canShare: aiInterviewer.currentUserCanManage ?? false,
      currentUserRole: undefined,
      optionsLoading: loading || usersLoading || (!usersData && !usersError),
      shareLoading,
      searchPlaceholder: "Find user by name",
      onDebouncedSearchChange: onSearchChange,
      shareUrl: `${window.location.origin}/ai-interviewer/${aiInterviewerId}`,

      onShare: async ({ userIds, role }) => {
        setShareOperation({ type: "share", loading: true });
        try {
          await setMemberships({
            variables: {
              aiInterviewerId,
              members: [
                // Keep existing memberships (excluding the ones being updated)
                ...(aiInterviewer.memberships || [])
                  .filter((m) => !userIds.includes(m.user.id))
                  .map((m) => ({
                    userId: m.user.id,
                    role: roleToGraphQLRole(
                      m.role as AiInterviewerMembershipRole
                    ),
                  })),
                // Add new/updated memberships
                ...userIds.map((userId) => ({
                  userId,
                  role: roleToGraphQLRole(role),
                })),
              ],
            },
          });
        } catch (err) {
          // Error handled by mutation onError
        }
      },

      onRemoveShare: async (shareId) => {
        setShareOperation({ type: "remove", loading: true });
        try {
          await removeMembership({
            variables: {
              membershipId: shareId,
            },
            optimisticResponse: {
              removeAiInterviewerMembership: {
                success: true,
                __typename: "RemoveAiInterviewerMembership",
              },
            },
          });
        } catch (err) {
          // Error handled by mutation onError
        }
      },

      onChangeRole: async (shareId, newRole) => {
        setShareOperation({ type: "change_role", loading: true });
        try {
          const membership = aiInterviewer.memberships?.find(
            (m) => m.id === shareId
          );
          if (!membership) {
            setShareOperation({ type: "idle" });
            errorToast(toast, "Failed to change role: Membership not found");
            return;
          }

          await setMemberships({
            variables: {
              aiInterviewerId,
              members: [
                // Keep all other memberships unchanged
                ...(aiInterviewer.memberships || [])
                  .filter((m) => m.id !== shareId)
                  .map((m) => ({
                    userId: m.user.id,
                    role: roleToGraphQLRole(
                      m.role as AiInterviewerMembershipRole
                    ),
                  })),
                // Update the changed membership
                {
                  userId: membership.user.id,
                  role: roleToGraphQLRole(newRole),
                },
              ],
            },
          });
        } catch (err) {
          // Error handled by mutation onError
        }
      },
    };
  }, [
    aiInterviewer,
    loading,
    aiInterviewerId,
    toast,
    shareableUsers,
    shareOperation,
    setMemberships,
    removeMembership,
    loadData,
    usersLoading,
    usersData,
    usersError,
  ]);

  return {
    shareModalProps,
    loadData,
    loading: loading || usersLoading || (!usersData && !usersError),
  };
}
